/**
 * Microsoft Advertising — Report-Request Body Builders (Story 19.2).
 *
 * Pure-data column lists + the `buildSubmitBody()` composer that combines a
 * column list with a date range + report-type discriminator into the JSON body
 * Microsoft's `SubmitGenerateReport` endpoint expects.
 *
 * All 8 report types pin to:
 *   - Format:        "Csv"
 *   - FormatVersion: "2.0"
 *   - ReturnOnlyCompleteData: false
 *   - Aggregation:   "Daily"
 *   - ReportTimeZone: "PacificTimeUSCanadaTijuana"
 *
 * The Pacific time zone is Microsoft's documented default (verified live in
 * the ReportRequest reference). Pinning it ensures day boundaries are
 * deterministic regardless of the campaign's configured time zone — agencies
 * comparing Microsoft Ads to Google Ads side-by-side need this. See the
 * "Time Zone Determinism" section of story 19.2 dev notes.
 *
 * Column-list invariants (asserted at runtime + via unit tests):
 *   1. No deprecated column ever appears (`Conversions`, `AllConversions`,
 *      `AveragePosition` — see MICROSOFT_ADS_DEPRECATED_COLUMNS).
 *   2. Impression-share columns and `TopVsOther` never appear together
 *      (Microsoft 400s on this combo per AC #7).
 */

import type { MicrosoftAdsReportType } from "./reporting";

// ============================================================================
// Deprecated column allowlist guard
// ============================================================================

/**
 * Columns Microsoft removed but still accepts (returning 0). They look
 * indistinguishable from real zeros in widgets, so we never request, return,
 * or normalise them. See `_bmad/bmm/data/integration-metrics/microsoft-ads.md`
 * — Deprecated section.
 *
 * Story 19.1 review #1 removed this constant as unused; story 19.2 re-introduces
 * it as the **single source of truth** for build-time + runtime guards.
 */
export const MICROSOFT_ADS_DEPRECATED_COLUMNS = [
  "Conversions",
  "AllConversions",
  "AveragePosition",
] as const;

const DEPRECATED_SET: ReadonlySet<string> = new Set(
  MICROSOFT_ADS_DEPRECATED_COLUMNS
);

/**
 * Impression-share columns that cannot be combined with `TopVsOther` per
 * Microsoft's columns-vs-aggregation matrix (AC #7).
 */
const IMPRESSION_SHARE_COLUMNS: ReadonlySet<string> = new Set([
  "ImpressionSharePercent",
  "ImpressionShareLostToBudgetPercent",
  "ImpressionShareLostToRankPercent",
  "ClickSharePercent",
  "AbsoluteTopImpressionRatePercent",
  "AbsoluteTopImpressionSharePercent",
  "AbsoluteTopImpressionShareLostToBudgetPercent",
  "AbsoluteTopImpressionShareLostToRankPercent",
  "TopImpressionRatePercent",
  "TopImpressionSharePercent",
  "TopImpressionShareLostToBudgetPercent",
  "TopImpressionShareLostToRankPercent",
  "AudienceImpressionSharePercent",
  "AudienceImpressionLostToBudgetPercent",
  "AudienceImpressionLostToRankPercent",
]);

/**
 * Asserts a column list contains no deprecated columns. Throws on violation —
 * the test suite calls this for every column-list constant on every run, and
 * it also runs at adapter init time for defence-in-depth.
 */
export function assertNoDeprecatedColumns(
  columns: readonly string[],
  reportType: string
): void {
  for (const col of columns) {
    if (DEPRECATED_SET.has(col)) {
      throw new Error(
        `Microsoft Ads ${reportType}: deprecated column "${col}" — use the *Qualified or modern impression-share variant`
      );
    }
  }
}

/**
 * Asserts a column list does not combine impression-share columns with
 * `TopVsOther`. Microsoft 400s on this combo (AC #7).
 */
export function assertNoColumnConflicts(
  columns: readonly string[],
  reportType: string
): void {
  if (!columns.includes("TopVsOther")) return;
  for (const col of columns) {
    if (IMPRESSION_SHARE_COLUMNS.has(col)) {
      throw new Error(
        `Microsoft Ads ${reportType}: cannot combine impression-share column "${col}" with "TopVsOther"`
      );
    }
  }
}

// ============================================================================
// Column lists per report type
// ============================================================================

/**
 * Core delivery / conversion / revenue columns shared by every report type.
 * Centralised here so a future column rename only needs one edit. Pinned to
 * the rows in `_bmad/bmm/data/integration-metrics/microsoft-ads.md`.
 */
const CORE_DELIVERY_COLUMNS = [
  "Impressions",
  "Clicks",
  "Ctr",
  "Spend",
  "AverageCpc",
  "AverageCpm",
] as const;

const CORE_CONVERSION_COLUMNS = [
  "ConversionsQualified",
  "AllConversionsQualified",
  "ConversionRate",
  "AllConversionRate",
  "CostPerConversion",
  "AllCostPerConversion",
  "ViewThroughConversions",
  "ViewThroughConversionRate",
  "ViewThroughCostPerConversion",
] as const;

const CORE_REVENUE_COLUMNS = [
  "Revenue",
  "AllRevenue",
  "ReturnOnAdSpend",
  "AllReturnOnAdSpend",
  "RevenuePerConversion",
  "AllRevenuePerConversion",
] as const;

export const OVERVIEW_COLUMNS = [
  "TimePeriod",
  "AccountId",
  "AccountName",
  "AccountNumber",
  "CurrencyCode",
  ...CORE_DELIVERY_COLUMNS,
  ...CORE_CONVERSION_COLUMNS,
  ...CORE_REVENUE_COLUMNS,
] as const;

export const CAMPAIGNS_COLUMNS = [
  "TimePeriod",
  "AccountId",
  "AccountName",
  "CampaignId",
  "CampaignName",
  "CampaignStatus",
  "CampaignType",
  "BidStrategyType",
  "CurrencyCode",
  ...CORE_DELIVERY_COLUMNS,
  "DailySpend",
  ...CORE_CONVERSION_COLUMNS,
  ...CORE_REVENUE_COLUMNS,
  // Impression share — campaign level
  "ImpressionSharePercent",
  "ImpressionShareLostToBudgetPercent",
  "ImpressionShareLostToRankPercent",
  "ClickSharePercent",
  "AbsoluteTopImpressionRatePercent",
  "AbsoluteTopImpressionSharePercent",
  "TopImpressionRatePercent",
  "TopImpressionSharePercent",
  "AudienceImpressionSharePercent",
] as const;

export const AD_GROUPS_COLUMNS = [
  "TimePeriod",
  "AccountId",
  "CampaignId",
  "CampaignName",
  "AdGroupId",
  "AdGroupName",
  "AdGroupStatus",
  "AdGroupType",
  "CurrencyCode",
  ...CORE_DELIVERY_COLUMNS,
  ...CORE_CONVERSION_COLUMNS,
  ...CORE_REVENUE_COLUMNS,
] as const;

export const KEYWORDS_COLUMNS = [
  "TimePeriod",
  "AccountId",
  "CampaignId",
  "CampaignName",
  "AdGroupId",
  "AdGroupName",
  "Keyword",
  "KeywordId",
  "KeywordStatus",
  "BidMatchType",
  "DeliveredMatchType",
  "CurrentMaxCpc",
  "CurrencyCode",
  ...CORE_DELIVERY_COLUMNS,
  ...CORE_CONVERSION_COLUMNS,
  ...CORE_REVENUE_COLUMNS,
  // Quality Score columns — search-only. Including them on display/audience
  // reports returns "--" strings (which would silently coerce to 0).
  "QualityScore",
  "HistoricalQualityScore",
  "QualityImpact",
  "ExpectedCtr",
  "HistoricalExpectedCtr",
  "AdRelevance",
  "HistoricalAdRelevance",
  "LandingPageExperience",
  "HistoricalLandingPageExperience",
] as const;

export const SEARCH_QUERIES_COLUMNS = [
  "TimePeriod",
  "AccountId",
  "CampaignId",
  "CampaignName",
  "AdGroupId",
  "AdGroupName",
  "Keyword",
  "SearchQuery",
  "DeliveredMatchType",
  "CurrencyCode",
  ...CORE_DELIVERY_COLUMNS,
  ...CORE_CONVERSION_COLUMNS,
  "Revenue",
  "AllRevenue",
] as const;

export const SHOPPING_COLUMNS = [
  "TimePeriod",
  "AccountId",
  "CampaignId",
  "CampaignName",
  "MerchantProductId",
  "ProductCategory1",
  "ProductCategory2",
  "ProductType1",
  "BenchmarkBid",
  "BenchmarkCtr",
  "CurrencyCode",
  ...CORE_DELIVERY_COLUMNS,
  ...CORE_CONVERSION_COLUMNS,
  "Revenue",
  "AllRevenue",
  "ReturnOnAdSpend",
] as const;

export const AGE_GENDER_COLUMNS = [
  "TimePeriod",
  "AccountId",
  "CampaignId",
  "CampaignName",
  "AdGroupId",
  "AdGroupName",
  "AgeGroup",
  "Gender",
  "CurrencyCode",
  ...CORE_DELIVERY_COLUMNS,
  ...CORE_CONVERSION_COLUMNS,
] as const;

export const GEO_COLUMNS = [
  "TimePeriod",
  "AccountId",
  "CampaignId",
  "CampaignName",
  "Country",
  "State",
  "City",
  "MetroArea",
  "PostalCode",
  "LocationType",
  "CurrencyCode",
  ...CORE_DELIVERY_COLUMNS,
  ...CORE_CONVERSION_COLUMNS,
] as const;

/**
 * Lookup table mapping our internal `MicrosoftAdsReportType` to the
 * canonical column list. Keeps `buildSubmitBody` data-driven.
 */
export const COLUMNS_BY_REPORT_TYPE: Record<
  MicrosoftAdsReportType,
  readonly string[]
> = {
  overview: OVERVIEW_COLUMNS,
  campaigns: CAMPAIGNS_COLUMNS,
  adGroups: AD_GROUPS_COLUMNS,
  keywords: KEYWORDS_COLUMNS,
  searchQueries: SEARCH_QUERIES_COLUMNS,
  shopping: SHOPPING_COLUMNS,
  ageGender: AGE_GENDER_COLUMNS,
  geo: GEO_COLUMNS,
};

// Module-load-time guard: every column list passes both invariants.
for (const [reportType, columns] of Object.entries(COLUMNS_BY_REPORT_TYPE)) {
  assertNoDeprecatedColumns(columns, reportType);
  assertNoColumnConflicts(columns, reportType);
}

// ============================================================================
// Per-report-type max date range
// ============================================================================

/**
 * Microsoft enforces per-report-type date-range maximums. We mirror them here
 * so the adapter can validate before submitting.
 *
 * - ShareOfVoice: 31 days
 * - Geographic:   1095 days (3 years)
 * - Default:      730 days (2 years) — Microsoft's published default ceiling
 */
export const MAX_DATE_RANGE_DAYS: Record<MicrosoftAdsReportType, number> = {
  overview: 730,
  campaigns: 730,
  adGroups: 730,
  keywords: 730,
  searchQueries: 730,
  shopping: 730,
  ageGender: 730,
  geo: 1095,
};

// ============================================================================
// Body builder
// ============================================================================

/**
 * Convert a Date into Microsoft's `{ Year, Month, Day }` triplet using UTC
 * accessors. UTC-derived to avoid Worker time-zone drift; Microsoft's
 * `ReportTimeZone` parameter handles the report's alignment independently.
 */
export function dateToYearMonthDay(d: Date): {
  Year: number;
  Month: number;
  Day: number;
} {
  return {
    Year: d.getUTCFullYear(),
    Month: d.getUTCMonth() + 1,
    Day: d.getUTCDate(),
  };
}

export interface SubmitBodyInput {
  reportType: MicrosoftAdsReportType;
  apiReportType: string; // e.g. "AccountPerformanceReportRequest"
  accountId: string;
  dateRange: { start: Date; end: Date };
  reportName?: string;
}

/**
 * Builds a `SubmitGenerateReport` body. The wire format Microsoft expects is:
 *
 *   {
 *     "ReportRequest": {
 *       "Type":            "<ApiReportType>",
 *       "ReportName":      "...",
 *       "Format":          "Csv",
 *       "FormatVersion":   "2.0",
 *       "ReturnOnlyCompleteData": false,
 *       "Aggregation":     "Daily",
 *       "Columns":         [...],
 *       "Scope":           { "AccountIds": [<accountId>] },
 *       "Time": {
 *         "CustomDateRangeStart": { Year, Month, Day },
 *         "CustomDateRangeEnd":   { Year, Month, Day },
 *         "ReportTimeZone":       "PacificTimeUSCanadaTijuana"
 *       }
 *     }
 *   }
 */
export function buildSubmitBody(input: SubmitBodyInput): {
  ReportRequest: Record<string, unknown>;
} {
  const columns = COLUMNS_BY_REPORT_TYPE[input.reportType];
  if (!columns) {
    throw new Error(
      `Microsoft Ads buildSubmitBody: unknown reportType "${input.reportType}"`
    );
  }
  // Defence-in-depth — re-run guards in case a future maintainer mutates the
  // column-list constants at runtime (yes, it has happened in other adapters).
  assertNoDeprecatedColumns(columns, input.reportType);
  assertNoColumnConflicts(columns, input.reportType);

  const reportName =
    input.reportName ??
    `ProvenLeap ${input.reportType} ${input.dateRange.start
      .toISOString()
      .slice(0, 10)} to ${input.dateRange.end.toISOString().slice(0, 10)}`;

  return {
    ReportRequest: {
      Type: input.apiReportType,
      ReportName: reportName,
      Format: "Csv",
      FormatVersion: "2.0",
      ReturnOnlyCompleteData: false,
      Aggregation: "Daily",
      Columns: [...columns],
      Scope: {
        AccountIds: [Number(input.accountId)],
      },
      Time: {
        CustomDateRangeStart: dateToYearMonthDay(input.dateRange.start),
        CustomDateRangeEnd: dateToYearMonthDay(input.dateRange.end),
        ReportTimeZone: "PacificTimeUSCanadaTijuana",
      },
    },
  };
}
